import { useWorkspace } from '@/features/workspaces/hooks';
import { useDebounce } from '@/hooks/useDebounce';
import { SpinnerIcon } from '@/lib';
import { AudienceSyncTimeBasedSchedule } from '@/models/audiences';
import { useLazyGetNextSchedulesQuery } from '@/services/endpoints/audiences';
import { format, parseISO } from 'date-fns';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSchedule } from './validation';

type AudienceSyncRepeatNextProps = {
  scheduleConfiguration: AudienceSyncTimeBasedSchedule;
};

export const AudienceSyncRepeatNext: FC<AudienceSyncRepeatNextProps> = ({ scheduleConfiguration }) => {
  const workspace = useWorkspace();
  const { t } = useTranslation('audience_edit');

  const [getNextSchedules, { data: nextScheduleData }] = useLazyGetNextSchedulesQuery();
  const [nextSchedules, setNextSchedules] = useState<string[]>();
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const debouncedConfiguration = useDebounce(scheduleConfiguration, 1000);

  useEffect(() => {
    setShowLoading(true);
  }, [scheduleConfiguration]);

  useEffect(() => {
    const errors = validateSchedule(
      {
        scheduleType: 'SCHEDULED',
        scheduleConfiguration,
      },
      t
    );

    if (Object.keys(errors).length === 0) {
      setShowLoading(true);
      getNextSchedules({
        workspaceId: workspace.id,
        times: 5,
        scheduleType: 'SCHEDULED',
        scheduleConfiguration: debouncedConfiguration,
      });
    } else {
      setNextSchedules(undefined);
    }
  }, [debouncedConfiguration]);

  useEffect(() => {
    if (nextScheduleData && nextScheduleData.nextSchedules) {
      setNextSchedules(nextScheduleData.nextSchedules);
      setShowLoading(false);
    }
  }, [nextScheduleData]);

  return nextSchedules && nextSchedules.length ? (
    <div>
      <div className='flex flex-row'>
        <span className='mr-2 font-semibold'>{t('theNextUpdatesWillBeOn')}:</span>
        {showLoading && <SpinnerIcon className='size-6 text-blue-900' />}
      </div>

      <ul className='list-inside list-disc'>
        {nextSchedules.map((schedule) => (
          <li key={schedule} className='ml-3 mt-1 font-normal leading-5 text-gray-700'>
            {format(parseISO(schedule), "EEEE, dd MMMM yyyy 'at' HH:mm")}
          </li>
        ))}
      </ul>
    </div>
  ) : (
    <div className={'text-gray-400'}>{t('onceSelectedTheRepeatFrequencyYouWillSeeTheNextUpdatesJustToBeSureThatItWasWhatYouMean')}</div>
  );
};
